import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, getInitials } from '@/lib/utils';

const avatarVariants = cva(
  'relative flex shrink-0 overflow-hidden rounded-full',
  {
    variants: {
      size: {
        xs: 'h-6 w-6 text-xs',
        sm: 'h-8 w-8 text-sm',
        md: 'h-10 w-10 text-base',
        lg: 'h-12 w-12 text-lg',
        xl: 'h-16 w-16 text-xl',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

export interface AvatarProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof avatarVariants> {
  src?: string;
  alt?: string;
  fallback?: string;
  // Aliases used in app code/demo
  initials?: string;
  name?: string;
}

export function Avatar({ 
  className, 
  size, 
  src, 
  alt, 
  fallback,
  initials,
  name,
  ...props 
}: AvatarProps) {
  const [imageError, setImageError] = React.useState(false);
  
  const showFallback = !src || imageError;
  const derivedFallback = fallback || name || initials || '?';
  const text = initials || (derivedFallback ? getInitials(derivedFallback) : '?');
  
  return (
    <div
      className={cn(avatarVariants({ size }), className)}
      {...props}
    >
      {!showFallback ? (
        <img
          src={src}
          alt={alt || derivedFallback}
          className="aspect-square h-full w-full object-cover"
          onError={() => setImageError(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-muted">
          <span className="font-medium text-muted-foreground">
            {text}
          </span>
        </div>
      )}
    </div>
  );
}

// Avatar group component for displaying multiple avatars
export interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  max?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export function AvatarGroup({ 
  children, 
  max = 3, 
  size = 'md',
  className,
  ...props 
}: AvatarGroupProps) {
  const childrenArray = React.Children.toArray(children);
  const displayedChildren = childrenArray.slice(0, max);
  const remainingCount = childrenArray.length - max;
  
  return (
    <div className={cn('flex -space-x-2', className)} {...props}>
      {displayedChildren.map((child, index) => (
        <div key={index} className="relative" style={{ zIndex: max - index }}>
          {React.isValidElement(child) && React.cloneElement(child as React.ReactElement<any>, {
            size,
            className: cn(
              (child as React.ReactElement<any>).props.className,
              'ring-2 ring-background'
            ),
          })}
        </div>
      ))}
      {remainingCount > 0 && (
        <div
          className={cn(
            avatarVariants({ size }),
            'flex items-center justify-center bg-muted ring-2 ring-background'
          )}
          style={{ zIndex: 0 }}
        >
          <span className="text-xs font-medium text-muted-foreground">
            +{remainingCount}
          </span>
        </div>
      )}
    </div>
  );
}
